import { QUESTIONS } from "../data/questions.js";
import { JOURNEYS, JOURNEY_ORDER, PATHWAYS } from "../data/journeys.js";
import { COMBINATIONS, FLAG_NOTES, PLACEMENT_NOTES, LATER_REASONS, CLOSE_SCORES_NOTE } from "../data/interpretations.js";

const PIDS = Object.keys(PATHWAYS);
const jOf = (pid) => PATHWAYS[pid].journey;
const sum = (a) => a.reduce((x, y) => x + y, 0);
const topSum = (vals, n) => sum([...vals].sort((a, b) => b - a).slice(0, n));

const journeySums = (opt) => {
  const out = Object.fromEntries(JOURNEY_ORDER.map((j) => [j, 0]));
  Object.entries(opt.w).forEach(([p, v]) => (out[jOf(p)] += v));
  return out;
};

// Maximum achievable points per pathway and per journey (used for normalization).
export const MAX = (() => {
  const pw = Object.fromEntries(PIDS.map((p) => [p, 0]));
  const jr = Object.fromEntries(JOURNEY_ORDER.map((j) => [j, 0]));
  QUESTIONS.forEach((q) => {
    const n = q.type === "multi" ? q.max || q.options.length : 1;
    PIDS.forEach((p) => (pw[p] += topSum(q.options.map((o) => o.w[p] || 0), n)));
    JOURNEY_ORDER.forEach((j) => (jr[j] += topSum(q.options.map((o) => journeySums(o)[j]), n)));
  });
  return { pw, jr };
})();

const selected = (q, a) => (Array.isArray(a) ? a : a === undefined || a === null ? [] : [a]).map((i) => q.options[i]);

export function scoreAnswers(answers) {
  const raw = Object.fromEntries(PIDS.map((p) => [p, 0]));
  const strength = Object.fromEntries(PIDS.map((p) => [p, 0]));
  const flags = {};
  QUESTIONS.forEach((q) => {
    selected(q, answers[q.id]).forEach((opt) => {
      Object.entries(opt.w).forEach(([p, v]) => (raw[p] += v));
      Object.entries(opt.s).forEach(([p, v]) => (strength[p] += v));
      opt.f.forEach((f) => (flags[f] = (flags[f] || 0) + 1));
    });
  });
  const rel = Object.fromEntries(PIDS.map((p) => [p, MAX.pw[p] ? raw[p] / MAX.pw[p] : 0]));
  const journeyScore = Object.fromEntries(
    JOURNEY_ORDER.map((j) => [j, sum(JOURNEYS[j].pathways.map((p) => raw[p])) / MAX.jr[j]])
  );
  return { raw, rel, strength, flags, journeyScore };
}

function priorities({ rel, strength, flags }) {
  const f = (k) => flags[k] || 0;
  const pr = { ...rel };
  const bump = (p, v) => (pr[p] += v);
  bump("EM", Math.min(0.45, 0.15 * (f("overwhelm") + f("disconnected"))));
  bump("WB", f("wombDisconnect") ? 0.3 : 0);
  bump("DF", Math.min(0.4, 0.1 * f("overdoing")));
  bump("SW", f("desireSensual") ? 0.3 : 0);
  bump("PR", Math.min(0.4, 0.1 * f("lowBoundaries")));
  bump("ID", Math.min(0.4, 0.12 * f("limitingBeliefs")));
  bump("EA", Math.min(0.45, 0.15 * f("intentNoAction")));
  ["RR", "SE"].forEach((p) => bump(p, Math.min(0.3, 0.1 * f("unsupportiveLifestyle"))));
  // Strong foundations: don't force introductory work first.
  PIDS.forEach((p) => {
    if (strength[p] >= 4 && rel[p] < 0.5) pr[p] -= 0.25;
  });
  if (strength.PR >= 4 && !f("lowBoundaries")) pr.PR -= 0.1;
  if (strength.EM >= 4 && !f("overwhelm")) pr.EM -= 0.1;
  return pr;
}

// Sequencing rules per journey: `front` pathways lead the order, `include` pathways must appear.
const RULES = {
  womb: (f) => ({
    front: [...(f("overwhelm") || f("disconnected") ? ["EM"] : []), ...(f("wombDisconnect") ? ["WB"] : [])],
    include: [...(f("overdoing") ? ["DF"] : []), ...(f("desireSensual") ? ["SW"] : [])],
  }),
  spirit: (f) => ({ front: f("lowBoundaries") ? ["PR"] : [], include: [] }),
  reality: (f) => ({ front: [...(f("limitingBeliefs") ? ["ID"] : []), ...(f("intentNoAction") ? ["EA"] : [])], include: [] }),
  lifestyle: (f) => ({ front: f("unsupportiveLifestyle") ? ["RR", "SE"] : [], include: [] }),
};

// A note is only shown if the final order actually reflects it.
const NOTE_CHECKS = {
  overwhelm: (q) => q[0] === "EM",
  disconnected: (q) => q[0] === "EM",
  wombDisconnect: (q) => q.indexOf("WB") >= 0 && q.indexOf("WB") <= 1,
  overdoing: (q) => q.includes("DF"),
  desireSensual: (q) => q.includes("SW"),
  lowBoundaries: (q) => q[0] === "PR",
  limitingBeliefs: (q) => q[0] === "ID",
  intentNoAction: (q) => q.indexOf("EA") >= 0 && q.indexOf("EA") <= 1,
  unsupportiveLifestyle: (q) => q.slice(0, 2).includes("RR") && q.slice(0, 2).includes("SE"),
};
const NOTE_JOURNEY = {
  overwhelm: "womb", disconnected: "womb", wombDisconnect: "womb", overdoing: "womb", desireSensual: "womb",
  lowBoundaries: "spirit", limitingBeliefs: "reality", intentNoAction: "reality", unsupportiveLifestyle: "lifestyle",
};

export function notesFor(jid, seq, flags) {
  const keys = Object.keys(FLAG_NOTES).filter((k) => NOTE_JOURNEY[k] === jid && flags[k] && NOTE_CHECKS[k](seq));
  const dedup = keys.includes("overwhelm") ? keys.filter((k) => k !== "disconnected") : keys;
  return dedup.map((k) => FLAG_NOTES[k]);
}

function sequenceFor(jid, pr, flags, { min, max, threshold = 0.3 }) {
  const f = (k) => flags[k] || 0;
  const all = [...JOURNEYS[jid].pathways].sort((a, b) => pr[b] - pr[a]);
  let seq = all.filter((p) => pr[p] >= threshold).slice(0, max);
  if (seq.length < min) seq = all.slice(0, min);
  const { front, include } = RULES[jid](f);
  const frontSorted = jid === "lifestyle" ? [...front].sort((a, b) => pr[b] - pr[a]) : front;
  seq = [...frontSorted, ...seq.filter((p) => !front.includes(p))];
  include.forEach((p) => !seq.includes(p) && seq.push(p));
  const keep = new Set([...front, ...include]);
  while (seq.length > max) {
    const i = [...seq].reverse().findIndex((p) => !keep.has(p));
    seq.splice(i >= 0 ? seq.length - 1 - i : seq.length - 1, 1);
  }
  const optional = all.filter((p) => !seq.includes(p));
  return { sequence: seq, optional };
}

function assignPlacements(journeyScore, flags) {
  const order = [...JOURNEY_ORDER].sort((a, b) => journeyScore[b] - journeyScore[a]);
  const notes = [];
  const swapToInterwoven = (jid, note) => {
    const i = order.indexOf(jid);
    if (i === 3) {
      [order[2], order[3]] = [order[3], order[2]];
      notes.push(note);
    }
  };
  if ((flags.unsupportiveLifestyle || 0) >= 2) swapToInterwoven("lifestyle", "lifestyle");
  if ((flags.overwhelm || 0) + (flags.disconnected || 0) >= 3) swapToInterwoven("womb", "womb");
  return { order, notes };
}

const SIZES = {
  primary: (j) => ({ min: 2, max: j === "lifestyle" ? 5 : 4 }),
  secondary: (j) => ({ min: 2, max: j === "lifestyle" ? 4 : 3 }),
  interwoven: () => ({ min: 1, max: 3, threshold: 0.35 }),
  later: () => ({ min: 1, max: 2, threshold: 0.35 }),
};
const PLACE_KEYS = ["primary", "secondary", "interwoven", "later"];

export function buildRoadmap(answers, firstName) {
  const s = scoreAnswers(answers);
  const pr = priorities(s);
  const { order, notes } = assignPlacements(s.journeyScore, s.flags);
  const placements = {};
  PLACE_KEYS.forEach((key, i) => {
    const jid = order[i];
    const seq = sequenceFor(jid, pr, s.flags, SIZES[key](jid));
    placements[key] = {
      key, journeyId: jid, journey: JOURNEYS[jid],
      score: Math.round(s.journeyScore[jid] * 100),
      sequence: seq.sequence, optional: seq.optional,
    };
  });

  const P = placements;
  PLACE_KEYS.forEach((k) => (P[k].flagNotes = k === "later" ? [] : notesFor(P[k].journeyId, P[k].sequence, s.flags)));
  const topNames = P.primary.sequence.slice(0, 2).map((p) => PATHWAYS[p].name);
  P.primary.explanation = `${firstName}, ${P.primary.journey.placement.primary}`;
  P.primary.highlight = `Your responses especially highlighted ${topNames.join(" and ")}.`;
  P.primary.why = PATHWAYS[P.primary.sequence[0]].whyNow;
  const close = s.journeyScore[P.primary.journeyId] - s.journeyScore[P.secondary.journeyId] < 0.05;
  P.secondary.explanation = P.secondary.journey.placement.secondary;
  P.secondary.builds = COMBINATIONS[`${P.primary.journeyId}-${P.secondary.journeyId}`];
  P.secondary.closeNote = close ? CLOSE_SCORES_NOTE : null;
  P.interwoven.explanation = P.interwoven.journey.placement.interwoven;
  P.interwoven.tips = P.interwoven.journey.interweaveTips;
  if (notes.includes(P.interwoven.journeyId)) P.interwoven.flagNotes.unshift(PLACEMENT_NOTES[P.interwoven.journeyId]);
  const laterStrength = sum(P.later.journey.pathways.map((p) => s.strength[p]));
  P.later.explanation = P.later.journey.placement.later;
  P.later.why = `${laterStrength >= 5 ? LATER_REASONS.strength : LATER_REASONS.lower} ${LATER_REASONS.always}`;

  const step = (pid, idx, label) => ({ pathway: PATHWAYS[pid].name, journey: JOURNEYS[jOf(pid)].name, action: PATHWAYS[pid].practices[idx], label });
  const steps = [
    step(P.primary.sequence[0], 0, "Begin here"),
    P.primary.sequence[1] ? step(P.primary.sequence[1], 0, "Deepen gently") : step(P.secondary.sequence[0], 0, "Deepen gently"),
    step(P.interwoven.sequence[0], 0, "Weave alongside"),
  ];
  return { placements, steps, scores: s };
}
